/**
 * The permission table (§8.3).
 *
 * One declarative table, shipped to both sides. The API enforces it with a guard; the
 * frontend reads the same table to grey a control **with a reason** (§8.4). The
 * frontend decides appearance, the backend decides permission — never the other way
 * round, and never two copies of the table.
 */

import type { OrgRole, TeamRole_ } from './primitives.js';

export type Capability =
  | 'project.view'
  | 'project.create'
  | 'project.edit'
  | 'project.editContext'
  | 'project.editCategoryScope'
  | 'project.manageMembers'
  | 'audit.view'
  | 'audit.create'
  | 'audit.answerItem'
  | 'audit.submit'
  | 'audit.approve'
  | 'audit.cancel'
  | 'finding.manage'
  | 'waiver.request'
  | 'waiver.approve'
  | 'gate.sign'
  | 'schedule.manage'
  | 'kpi.record'
  | 'checklist.admin'
  | 'users.admin';

/** How much more than the org role is needed. */
export type PolicyRequirement =
  | 'allow'
  /** Requires a ProjectMember row on the project in question. */
  | 'member'
  /** Requires the item to be assigned to this user (or membership plus an auditor role). */
  | 'assignee'
  /** Requires holding a specific project role — checked per capability, see below. */
  | 'projectRole'
  | 'deny';

export const POLICY: Record<Capability, Record<OrgRole, PolicyRequirement>> = {
  'project.view': {
    Admin: 'allow',
    DeliveryManager: 'allow',
    Auditor: 'allow',
    Member: 'allow',
    ReadOnly: 'allow',
  },
  'project.create': {
    Admin: 'allow',
    DeliveryManager: 'allow',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'project.edit': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'project.editContext': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  /**
   * A second, coarser scope gate beside the context profile (issue #2). It changes which
   * items a new audit covers, exactly like the context profile does, so it carries the
   * same requirement.
   */
  'project.editCategoryScope': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'project.manageMembers': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'audit.view': {
    Admin: 'allow',
    DeliveryManager: 'allow',
    Auditor: 'allow',
    Member: 'allow',
    ReadOnly: 'allow',
  },
  'audit.create': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'audit.answerItem': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    // A Member edits only items assigned to them (§8.3).
    Member: 'assignee',
    ReadOnly: 'deny',
  },
  'audit.submit': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'audit.approve': {
    // Approval closes the audit and freezes the score. Deliberately narrow.
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'audit.cancel': {
    // Abandoning an audit is irreversible (it becomes a frozen record), so it is the
    // approver's call, not the auditor's.
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'finding.manage': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'assignee',
    ReadOnly: 'deny',
  },
  'waiver.request': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'waiver.approve': {
    /**
     * Needs PM *and* PO, and the same person cannot supply both signatures (§8.3).
     *
     * Every row is `projectRole`, which already requires holding PM or PO **on that
     * project** — the org role alone never grants this. `Member` and `ReadOnly` are
     * included deliberately: the Product Owner is frequently the client contact, and in
     * the seeded dataset C. Fischer is exactly that (`@novaris.example`, ReadOnly). With
     * ReadOnly denied outright, §3.8's PM+PO rule was unsatisfiable for the intended cast
     * — the customer could never accept a risk on their own project, which is the one
     * signature a waiver most needs.
     *
     * A ReadOnly user with no project role still cannot approve anything.
     */
    Admin: 'projectRole',
    DeliveryManager: 'projectRole',
    Auditor: 'projectRole',
    Member: 'projectRole',
    ReadOnly: 'projectRole',
  },
  'gate.sign': {
    // Only the holder of that project role may sign that row.
    Admin: 'projectRole',
    DeliveryManager: 'projectRole',
    Auditor: 'projectRole',
    Member: 'projectRole',
    ReadOnly: 'deny',
  },
  'schedule.manage': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  /**
   * Recording a KPI figure (ADR 0010). Like answering an item: it is a claim made under
   * your own name, so it needs membership, not merely a login.
   */
  'kpi.record': {
    Admin: 'allow',
    DeliveryManager: 'member',
    Auditor: 'member',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'checklist.admin': {
    Admin: 'allow',
    DeliveryManager: 'deny',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
  'users.admin': {
    Admin: 'allow',
    DeliveryManager: 'deny',
    Auditor: 'deny',
    Member: 'deny',
    ReadOnly: 'deny',
  },
};

/** Which project roles may approve a waiver — both are needed, from two people. */
export const WAIVER_APPROVAL_ROLES: readonly TeamRole_[] = ['PM', 'PO'];

export interface PolicySubject {
  readonly orgRole: OrgRole;
  /** Project roles this user holds on the resource in question. Empty = not a member. */
  readonly projectRoles: readonly TeamRole_[];
  /** True when the resource is an item assigned to this user. */
  readonly isAssignee?: boolean;
  /** Set for `gate.sign`: the row being signed. */
  readonly signingRole?: TeamRole_;
}

export interface PolicyVerdict {
  readonly allowed: boolean;
  /**
   * Why not — rendered on hover on a greyed control (§8.4). An auditor who cannot see
   * the Approve button assumes the tool is broken; one who sees it greyed with
   * "Only the Delivery Manager can approve" understands the system.
   */
  readonly reason?: string;
}

const ALLOWED: PolicyVerdict = { allowed: true };

export function can(
  capability: Capability,
  subject: PolicySubject,
  options: { auditClosed?: boolean } = {},
): PolicyVerdict {
  if (options.auditClosed === true && capability !== 'audit.view' && capability !== 'project.view') {
    return { allowed: false, reason: 'This audit is closed. A closed audit is a historical record and cannot be changed.' };
  }

  const requirement = POLICY[capability][subject.orgRole];
  const isMember = subject.projectRoles.length > 0;

  switch (requirement) {
    case 'allow':
      return ALLOWED;

    case 'deny':
      return {
        allowed: false,
        reason: `Your role (${subject.orgRole}) cannot do this.`,
      };

    case 'member':
      return isMember
        ? ALLOWED
        : { allowed: false, reason: 'Only members of this project can do this.' };

    case 'assignee':
      if (subject.isAssignee === true) return ALLOWED;
      return {
        allowed: false,
        reason: 'You can only change items that are assigned to you.',
      };

    case 'projectRole': {
      if (capability === 'gate.sign') {
        const row = subject.signingRole;
        if (row === undefined) {
          return { allowed: false, reason: 'No sign-off row was specified.' };
        }
        return subject.projectRoles.includes(row)
          ? ALLOWED
          : {
              allowed: false,
              reason: `Only the project's ${row} can sign this row.`,
            };
      }

      const holdsApprovalRole = subject.projectRoles.some((role) =>
        WAIVER_APPROVAL_ROLES.includes(role),
      );
      return holdsApprovalRole
        ? ALLOWED
        : {
            allowed: false,
            reason: 'A waiver needs the Project Manager and the Product Owner, from two different people.',
          };
    }
  }
}
